import * as Yup from 'yup';
import { fbValidations, validations } from '../services/validation';

// Schema for creating an ad set
export const createAdSetSchema = Yup.object().shape({
  campaign_id: validations.uuid,

  name: fbValidations.adSetName,

  status: Yup.string()
    .default('PAUSED')
    .oneOf(['ACTIVE', 'PAUSED'], 'Status must be Active or Paused'),

  // Budget fields (mutually exclusive)
  daily_budget: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(1, 'Daily budget must be at least $1')
    .test(
      'either-daily-or-lifetime',
      'Cannot set both daily and lifetime budget',
      function (value) {
        const { lifetime_budget } = this.parent;
        if (value && lifetime_budget) {
          return false;
        }
        return true;
      }
    ),

  lifetime_budget: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(1, 'Lifetime budget must be at least $1')
    .test(
      'either-daily-or-lifetime',
      'Cannot set both daily and lifetime budget',
      function (value) {
        const { daily_budget } = this.parent;
        if (value && daily_budget) {
          return false;
        }
        return true;
      }
    ),

  // Bid amount (optional)
  bid_amount: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(0.01, 'Bid amount must be at least $0.01'),

  // Billing event
  billing_event: fbValidations.billingEvent,

  // Optimization goal
  optimization_goal: fbValidations.optimizationGoal,

  // Schedule (required for lifetime budget)
  start_time: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    ),

  end_time: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .test(
      'end-after-start',
      'End time must be after start time',
      function (value) {
        const { start_time } = this.parent;
        if (value && start_time) {
          return new Date(value) > new Date(start_time);
        }
        return true;
      }
    )
    .test(
      'required-for-lifetime',
      'End time is required when using lifetime budget',
      function (value) {
        const { lifetime_budget } = this.parent;
        if (lifetime_budget && !value) {
          return false;
        }
        return true;
      }
    ),

  // Targeting (object)
  targeting: Yup.object().nullable(),
});

// Schema for updating an ad set
export const updateAdSetSchema = Yup.object().shape({
  name: fbValidations.adSetName.optional(),

  status: Yup.string()
    .optional()
    .oneOf(['ACTIVE', 'PAUSED', 'DELETED', 'ARCHIVED'], 'Invalid status'),

  daily_budget: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(1, 'Daily budget must be at least $1'),

  lifetime_budget: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(1, 'Lifetime budget must be at least $1'),

  bid_amount: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || Number.isNaN(originalValue) ? null : value
    )
    .min(0.01, 'Bid amount must be at least $0.01'),

  start_time: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    ),

  end_time: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    ),

  targeting: Yup.object().nullable(),
});

// Type inference from schema
export type CreateAdSetFormData = Yup.InferType<typeof createAdSetSchema>;
export type UpdateAdSetFormData = Yup.InferType<typeof updateAdSetSchema>;
